import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '@data/other/withStore';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import TimeUtils from '@business/utils/time';
import { startServer, stopServer } from '@data/mirage';

const DataObserver = () => {
  const { property, components, environment } = useStore();
  const { propertyCode } = useParams();
  const { checkin, checkout, code } = components.promoCode.value;
  const { pathname } = useLocation();
  const { language, currency } = environment;
  const [searchParams, setSearchParams] = useSearchParams({});

  const { accommodations, availabilityCalendar, reservation } = property;

  const [_reservationCode, setReservationCode] = useState<string>('');

  const loadInitialData = useCallback(async () => {
    if (propertyCode?.indexOf('mock') === 0) {
      startServer();
    }

    if (propertyCode) {
      const dataRes = searchParams.get('data_res');
      if (dataRes && dataRes.trim() !== '') {
        setReservationCode(dataRes);
        await reservation.get(dataRes);
      }
      await property.get(propertyCode);
    }

    return () => {
      if (propertyCode?.indexOf('mock') === 0) {
        stopServer();
      }
    };
  }, [propertyCode]);

  useEffect(() => {
    loadInitialData();
  }, [propertyCode]);

  useEffect(() => {
    if (property.model) {
      environment.setCurrency(property.model.defaultCurrency);
      accommodations.loadAccommodationTypes(property.model.id);
      const startDate = TimeUtils.format(TimeUtils.getToday());
      const endDate = TimeUtils.format(TimeUtils.getDateOffset(0, 0, 2));
      availabilityCalendar.search({
        propertyId: property.model.id,
        startDate,
        endDate,
      });
    }
  }, [property.model]);

  useEffect(() => {
    if (property.model) {
      accommodations.search(checkin, checkout);
    }
  }, [property.model, checkin, checkout]);

  // observing the query params
  useEffect(() => {
    if (!property.model) return;

    const query = {};
    if (code) {
      query['code'] = code;
    }
    if (checkin) {
      query['checkin'] = TimeUtils.format(checkin);
    }
    if (checkout) {
      query['checkout'] = TimeUtils.format(checkout);
    }
    if (language) {
      query['language'] = language;
    }
    if (currency) {
      query['currency'] = currency;
    }

    if (reservation.reservationCode) {
      query['data_res'] = reservation.reservationCode;
    }

    setSearchParams(query);
  }, [property, language, currency, checkin, checkout, code, pathname]);

  return null;
};

export default observer(DataObserver);
